import { useState } from "react";
import { Phone, Mail, MapPin, Clock, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteLayout from "@/components/SiteLayout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    area: "",
    monthly_bill: "",
    message: "",
  });

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.full_name.trim() || !form.phone.trim()) {
      toast({ title: "Please fill in your name and phone number", variant: "destructive" });
      return;
    }

    if (form.phone.replace(/\D/g, "").length < 10) {
      toast({ title: "Please enter a valid phone number", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-contact", {
        body: {
          full_name: form.full_name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() || undefined,
          area: form.area.trim() || undefined,
          monthly_bill: form.monthly_bill.trim() || undefined,
          message: form.message.trim() || undefined,
        },
      });

      if (error) throw error;

      setSubmitted(true);
      toast({ title: "Request submitted successfully!" });
    } catch (err) {
      console.error("Submit error:", err);
      toast({
        title: "Failed to submit",
        description: "Please try again or call us directly.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteLayout>
      {/* Header */}
      <section className="bg-gradient-navy py-20">
        <div className="container mx-auto px-4 text-center">
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">Get In Touch</span>
          <h1 className="text-3xl sm:text-5xl font-heading font-extrabold text-secondary-foreground mt-3">
            Contact Us
          </h1>
          <p className="text-secondary-foreground/60 mt-4 max-w-lg mx-auto">
            Request a free site survey or ask us anything about solar energy.
          </p>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div className="lg:col-span-3 bg-card border border-border rounded-xl p-8 shadow-card">
              <h2 className="text-xl font-heading font-bold text-card-foreground mb-6">Request Free Site Survey</h2>
              {submitted ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-card-foreground mb-2">Thank You!</h3>
                  <p className="text-muted-foreground">We'll call you within 24 hours to schedule your free site survey.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-medium text-card-foreground block mb-1.5">Full Name *</label>
                      <input required type="text" maxLength={100} value={form.full_name} onChange={(e) => updateField("full_name", e.target.value)} className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" placeholder="Your name" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-card-foreground block mb-1.5">Phone Number *</label>
                      <input required type="tel" maxLength={15} value={form.phone} onChange={(e) => updateField("phone", e.target.value)} className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" placeholder="+91 XXXXX XXXXX" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-card-foreground block mb-1.5">Email</label>
                    <input type="email" maxLength={255} value={form.email} onChange={(e) => updateField("email", e.target.value)} className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" placeholder="your@email.com" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-medium text-card-foreground block mb-1.5">Area / Locality</label>
                      <input type="text" maxLength={100} value={form.area} onChange={(e) => updateField("area", e.target.value)} className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" placeholder="e.g. Malviya Nagar" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-card-foreground block mb-1.5">Monthly Electricity Bill</label>
                      <input type="text" maxLength={50} value={form.monthly_bill} onChange={(e) => updateField("monthly_bill", e.target.value)} className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" placeholder="e.g. ₹3,000" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-card-foreground block mb-1.5">Message (Optional)</label>
                    <textarea rows={4} maxLength={1000} value={form.message} onChange={(e) => updateField("message", e.target.value)} className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none" placeholder="Tell us about your rooftop or energy needs..." />
                  </div>
                  <Button type="submit" variant="hero" size="lg" className="w-full gap-2" disabled={loading}>
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
                    {loading ? "Submitting..." : "Submit Request"}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact details */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-card border border-border rounded-xl p-6 shadow-card">
                <h3 className="font-heading font-semibold text-card-foreground mb-4">Contact Information</h3>
                <div className="space-y-5">
                  <a href="tel:9145863773" className="flex items-start gap-3 group">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Phone className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-card-foreground group-hover:text-primary transition-colors">+91 91458 63773</p>
                      <p className="text-xs text-muted-foreground">Call or WhatsApp</p>
                    </div>
                  </a>
                  <a href="tel:9784558338" className="flex items-start gap-3 group">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Phone className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-card-foreground group-hover:text-primary transition-colors">+91 97845 58338</p>
                      <p className="text-xs text-muted-foreground">Call or WhatsApp</p>
                    </div>
                  </a>
                  <a href="mailto:Nikchoudhary0909@gmail.com" className="flex items-start gap-3 group">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Mail className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-card-foreground group-hover:text-primary transition-colors">Nikchoudhary0909@gmail.com</p>
                      <p className="text-xs text-muted-foreground">Email us anytime</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <MapPin className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-card-foreground">301, 3rd Floor, P. No. 8, near VPM Classes, B Yojna, Radha Kunj, Mansarovar, Jaipur, Rajasthan 302020</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      <Clock className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-card-foreground">Mon – Sat, 9 AM – 7 PM</p>
                      <p className="text-xs text-muted-foreground">Sunday closed</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl overflow-hidden shadow-card">
                <iframe
                  title="ShashiSun Energy Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d227748.99973450298!2d75.65046970649679!3d26.88514167956319!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396c4adf4c57e281%3A0xce1c63a0cf22e09!2sJaipur%2C%20Rajasthan!5e0!3m2!1sen!2sin!4v1709000000000!5m2!1sen!2sin"
                  width="100%"
                  height="220"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default ContactPage;
